import { IconButton, Slide, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { FAIcon } from '@packages/ui-components';
import { X } from 'lucide-react';
import {
  trackFlowStart,
  trackFlowStepView,
  trackFlowStepComplete,
  trackFlowFinish,
  trackFlowExit,
} from '../../../utils/flowAnalytics';
import { TeleportBtnIcon } from '../icons/TeleportBtnIcon';
import {
  ActionSectionStyle,
  ActionsWrapperStyle,
  FlowbarBodyStyle,
  FlowbarWrapperStyle,
  ImageContainerStyle,
  PrevButtonStyle,
  TPButtonStyle,
  TriggerButtonStyle,
} from './styles';
import { useConditions } from '../hooks/useConditions';
import { ActionTypes } from '../types';
import { Exitbar } from './Exitbar';
import { FlowbarProps, TFlowFinishCondition } from './types';
import { FlowCloseDialog } from './FlowCloseDialog';
import { FlowSwitchDialog } from './FlowSwitchDialog';
import DeviceManager from '@/components/util/deviceManager';
import { FlowsFinishScreen } from './FlowsFinishScreen';
import { getVersion } from '@/components/util';
import { calculateStepPositionInPath, combineHiddenAssets, revertConditionsByStep } from '../utils';

import { useSocket } from '../hooks/useSocket';

const METERS_TO_FEET_CONVERSION = 3.28084;

export const Flowbar = ({
  flow,
  flows,
  selectedFlow,
  handleCloseFlowbar,
  moveToSweep,
  hideElements,
  showElements,
  showCaseTemp,
  showcase,
  tags,
  setFlows,
  polygons,
  vsObjects,
  navigationManager,
  allSweeps,
  currentFloor,
  closeSwitchDialog,
  isSwitchDialogOpen,
  handleSwitchAgree,
  flowsVisibleAssets,
  tour,
  flowsBarMeshComponent,
  flowsMenuMeshComponent,
  handleEmitActionBySocket,
  socket,
  isLocked,
  isRemoteControlled,
}: FlowbarProps) => {
  const innerSettings = useMemo(() => flow?.flowSetting, [flow]);
  const [isCloseDialogOpen, setIsCloseDialogOpen] = useState(false);
  const [showFinishScreen, setShowFinishScreen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [stepStartTime, setStepStartTime] = useState<number | null>(null);

  const flowsRef = useRef(flows);
  useEffect(() => {
    flowsRef.current = flows;
  }, [flows]);

  const {
    steps,
    stepsEdges,
    currentStep,
    setCurrentStep,
    executeNextStepTrigger,
    distance,
    handleTeleportToStep,
    executeConditionsInOrder,
    executedConditions,
    showFlowBar,
    setShowFlowBar,
    nextButtons,
  } = useConditions({
    flow,
    hideElements,
    moveToSweep,
    showcase,
    showCaseTemp,
    tags,
    showElements,
    setFlows,
    polygons,
    vsObjects,
    navigationManager,
    allSweeps,
    currentFloor,
    flowsVisibleAssets,
    handleEmitActionBySocket,
    socket,
  });
  const { finishScreenTitle = '' } = currentStep?.finishStepSettings || {};

  const hasAtLeastOneAction = useMemo(
    () =>
      innerSettings?.enableStepsView ||
      innerSettings?.enableDistanceFromTheDistination ||
      innerSettings?.enableExitButton,
    [innerSettings],
  );
  const { currentStepIndex, pathLength } = useMemo(() => {
    const { currentStepPosition, totalStepsInPath } = calculateStepPositionInPath(
      steps,
      stepsEdges,
      currentStep,
    );
    return { currentStepIndex: currentStepPosition, pathLength: totalStepsInPath };
  }, [steps, currentStep, stepsEdges]);

  const moveToPrevStep = useCallback(
    async (_, preventEmit = false) => {
      if (!currentStep) return;
      const prevStepEdge = stepsEdges?.find(
        (edge) => edge.target === currentStep.id && edge.trigger === currentStep.triggeredBy,
      );
      const startStepEdge = stepsEdges?.find(
        (edge) => edge.source === steps?.find((step) => step.type === 'start_step')?.id,
      );
      if (!startStepEdge || !prevStepEdge) return;
      const prevStep = steps?.find((step) => step.id === prevStepEdge.source);

      const revertConditions = revertConditionsByStep(
        executedConditions.current,
        currentStep.id,
        prevStep.id,
        startStepEdge,
      );

      if (revertConditions.length) {
        executedConditions?.current.delete(currentStep.id);
        executedConditions?.current.delete(prevStep.id);

        await executeConditionsInOrder(revertConditions, false, true);
      }

      const prevStepDelay = prevStep?.conditions
        ?.find((c) => c.action === 'start')
        ?.rows?.find((r) => r.action === ActionTypes.DELAY_FLOWBAR);
      if (prevStepDelay) {
        setShowFlowBar(false);
      }

      if (prevStep) setCurrentStep(prevStep);
      setIsExpanded(false);
      if (!preventEmit) handleEmitActionBySocket('flow_moveto_prev_step');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentStep, steps, stepsEdges],
  );

  const moveToStep = useCallback(
    (_?: any, preventEmit = false) => {
      handleTeleportToStep(currentStep);
      if (!preventEmit) handleEmitActionBySocket('flow_moveto_step');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentStep],
  );

  const description = currentStep?.description;

  const openCloseDialog = (_, preventEmit = false) => {
    setIsCloseDialogOpen(true);
    if (!preventEmit) handleEmitActionBySocket('open_close_flow_dialog');
  };

  const cancelCloseDialog = (_, preventEmit = false) => {
    setIsCloseDialogOpen(false);
    if (!preventEmit) handleEmitActionBySocket('cancel_flow_dialog');
  };

  const revertAllConditions = useCallback(
    async (switchFlowHideConditions?: any[], isFinishedFlow = false) => {
      let allHiddenConditions = Array.from(executedConditions.current.values())
        .flatMap((set) => Array.from(set))
        ?.filter(
          (c: any) => c.action === ActionTypes.HIDE_ASSET || c.action === ActionTypes.HIDE_SWEEP,
        );

      // if the flow that is being switched into has hide conditions that were already executed in current flow, we don't need to revert
      allHiddenConditions = combineHiddenAssets(allHiddenConditions, switchFlowHideConditions);

      if (allHiddenConditions?.length) {
        await executeConditionsInOrder(allHiddenConditions, isFinishedFlow);
      }

      executedConditions.current.clear();
      navigationManager.reset();

      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [executedConditions],
  );

  const onCloseAgree = useCallback(
    async (_, preventEmit = false) => {
      if (flow?.id && currentStep) {
        const duration = stepStartTime
          ? Math.round((Date.now() - stepStartTime) / 1000)
          : undefined;
        trackFlowExit(flow.id, currentStep.id, duration);
      }

      await revertAllConditions();
      await handleCloseFlowbar();
      setIsCloseDialogOpen(false);
      navigationManager.reset();
      setCurrentStep(null);
      if (!preventEmit) handleEmitActionBySocket('close_flow_agree');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [executedConditions, stepStartTime],
  );

  const onSwitchAgree = useCallback(async (switchFlow) => {
    const switchFlowHideConditions = switchFlow?.steps
      ?.find((step) => step.type === 'start_step')
      ?.conditions[0]?.rows?.filter((r) => r.action === 'hide_asset');
    revertAllConditions(switchFlowHideConditions);
    handleSwitchAgree(switchFlow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      if (currentStep && currentStep.type === 'finish_step') {
        if (flow?.id) {
          const duration = stepStartTime
            ? Math.round((Date.now() - stepStartTime) / 1000)
            : undefined;
          trackFlowFinish(flow.id, duration);
        }

        const nextFlowId =
          currentStep.finishCondition === TFlowFinishCondition.NextFlow
            ? currentStep.nextFlowId
            : null;
        if (nextFlowId) {
          const nextFlow = (flowsRef.current || []).find((f) => f.id === nextFlowId);
          if (nextFlow) {
            await onSwitchAgree({
              ...nextFlow,
              isNextFlow: true,
              restart: nextFlow?.steps
                ?.filter((s) => s.type === 'regular_step')
                .every((s) => s.completed),
            });
            return;
          }
        }
        if (currentStep.finishCondition === TFlowFinishCondition.FinishScreen) {
          setShowFinishScreen(true);
          return;
        }
        await revertAllConditions(null, true);
        await handleCloseFlowbar();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  useEffect(() => {
    if (currentStep && flow?.id) {
      // Only track analytics for regular steps and finish steps, not start steps
      // Start steps are automatically transitioned and shouldn't be tracked as user interactions
      if (currentStep.type !== 'start_step') {
        trackFlowStepView(flow.id, currentStep.id);
      }
      setStepStartTime(Date.now());
    }
  }, [currentStep, flow?.id, flow?.name]);

  // Track flow start when flow is first loaded
  useEffect(() => {
    if (flow?.id && currentStep?.type === 'start_step') {
      trackFlowStart(flow.id);
    }
  }, [flow?.id]);

  const handleVRNextButtonClick = () => {
    console.log('handleVRNextButtonClick', nextButtons);
    if (!nextButtons?.length) return;
    if (flowsMenuMeshComponent?.inputs?.visible) return;
    executeNextStepTrigger(nextButtons[0], stepStartTime);
  };

  const handleVRPositionButtonClick = () => {
    if (flowsMenuMeshComponent?.inputs?.visible) return;
    moveToStep();
  };

  const handleVRRestartButtonClick = () => {
    if (!flows || !flows.length) {
      console.error('❌ No flows available for restart');
      return;
    }

    // Find the main flow (isMain: true)
    const mainFlow = flows.find((f) => f.isMain === true);

    if (!mainFlow) {
      const firstFlow = flows[0];
      console.log('🎯 Restarting with first flow:', firstFlow);
      onSwitchAgree({ ...firstFlow, restart: true });
      return;
    }

    console.log('🎯 Restarting with main flow:', mainFlow);
    onSwitchAgree({ ...mainFlow, restart: true });
  };

  const handleVRThumbstickClick = async () => {
    // Check if VR Image Trigger is enabled and has an image
    if (!tour?.patchIcon) {
      console.log('VR Image Trigger not enabled or no image configured');
      return;
    }
  };

  useEffect(() => {
    if (currentStep && flowsBarMeshComponent) {
      const { html } = flowsBarMeshComponent.inputs;
      html.querySelector('.flows-bar-title').innerHTML = currentStep.title;
      html.querySelector('.flows-bar-description').innerHTML = currentStep.description;
      flowsBarMeshComponent.inputs.itemRefArr[0].style.display =
        innerSettings.enablePreviousButton && currentStepIndex > 1 && nextButtons?.length <= 1
          ? 'flex'
          : 'none';
      flowsBarMeshComponent.inputs.itemRefArr[1].style.display =
        innerSettings.enableNextButton && nextButtons?.length ? 'flex' : 'none';
      flowsBarMeshComponent.inputs.itemRefArr[2].style.display =
        innerSettings.enableTeleportButton && !currentStep?.hidePosBtn ? 'flex' : 'none';

      // Only DOM event listeners for the flow bar mesh component
      flowsBarMeshComponent.inputs.itemRefArr[0].addEventListener('click', moveToPrevStep);
      flowsBarMeshComponent.inputs.itemRefArr[1].addEventListener('click', handleVRNextButtonClick);
      flowsBarMeshComponent.inputs.itemRefArr[2].addEventListener('click', moveToStep);
    }
    return () => {
      if (flowsBarMeshComponent) {
        flowsBarMeshComponent.inputs.itemRefArr[0].removeEventListener('click', moveToPrevStep);
        flowsBarMeshComponent.inputs.itemRefArr[1].removeEventListener(
          'click',
          handleVRNextButtonClick,
        );
        flowsBarMeshComponent.inputs.itemRefArr[2].removeEventListener('click', moveToStep);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, nextButtons, isLocked, selectedFlow]);

  useEffect(() => {
    if (flowsBarMeshComponent && !selectedFlow) flowsBarMeshComponent.setVisible(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFlow]);

  const backgroundImagePath = tour.introScreen?.backgroundImage
    ? `${process.env.NEXT_PUBLIC_CDN}/tours/${tour.id}/images/${
        tour.introScreen?.backgroundImage
      }${getVersion(tour.updatedAt)}`
    : `${process.env.NEXT_PUBLIC_CDN}/tours/${tour.id}/images/${tour.mainImage}${getVersion(
        tour.updatedAt,
      )}`;

  const secondaryButtonExpanded = useMemo(
    () =>
      (!nextButtons?.length && !currentStep?.title && !description) ||
      (DeviceManager.isMobile() && nextButtons?.length >= 2),
    [nextButtons, currentStep, description],
  );

  const displayDistance = useMemo(() => {
    if (!distance) return '';
    const metersVal = Number(distance);
    if (tour?.calculateDistanceIn === 'ft') {
      return `${(metersVal * METERS_TO_FEET_CONVERSION).toFixed(1)}ft to go`;
    }
    return `${distance}m to go`;
  }, [distance, tour?.calculateDistanceIn]);

  const hexToRgbA = (hex, opacity) => {
    let c;
    if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
      c = hex.substring(1).split('');
      if (c.length === 3) {
        c = [c[0], c[0], c[1], c[1], c[2], c[2]];
      }
      c = `0x${c.join('')}`;
      // eslint-disable-next-line no-bitwise
      return `rgba(${[(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',')},${opacity})`;
    }
    return 'rgba(255,255,255,0.25)';
  };

  const handleNextButtonClick = (nb, preventEmit = false) => {
    // Analytics are now handled in executeNextStepTrigger
    executeNextStepTrigger(nb, stepStartTime);
    if (!preventEmit) handleEmitActionBySocket('flow_next_btn_click', nb);
  };

  useSocket({
    socket,
    isLocked,
    openCloseDialog,
    onCloseAgree,
    cancelCloseDialog,
    handleNextButtonClick,
    moveToPrevStep,
    moveToStep,
  });

  const handleVRFlowSelect = (e) => {
    onSwitchAgree({ ...e.detail, restart: true });
  };

  // Handle VR events from centralized BaseTour VR manager
  const handleVRFlowNextStep = () => {
    handleVRNextButtonClick();
  };

  const handleVRFlowPosition = () => {
    handleVRPositionButtonClick();
  };

  const handleVRFlowRestartEvent = () => {
    handleVRRestartButtonClick();
  };

  const handleVRThumbstickClickREvent = () => {
    handleVRThumbstickClick();
  };

  useEffect(() => {
    // Listen to VR flow events from centralized BaseTour VR manager
    document.addEventListener('vrFlowSelect', handleVRFlowSelect);
    document.addEventListener('vrFlowNextStep', handleVRFlowNextStep);
    document.addEventListener('vrFlowPosition', handleVRFlowPosition);
    document.addEventListener('vrFlowRestart', handleVRFlowRestartEvent);

    document.addEventListener('vrThumbstickClickR', handleVRThumbstickClickREvent);

    return () => {
      document.removeEventListener('vrFlowSelect', handleVRFlowSelect);
      document.removeEventListener('vrFlowNextStep', handleVRFlowNextStep);
      document.removeEventListener('vrFlowPosition', handleVRFlowPosition);
      document.removeEventListener('vrFlowRestart', handleVRFlowRestartEvent);
    };
  }, [currentStep, flow, nextButtons]);

  return (
    <>
      {showFinishScreen && (
        <FlowsFinishScreen
          title={finishScreenTitle}
          finishScreenImage={
            currentStep?.url
              ? `${process.env.NEXT_PUBLIC_CDN}/tours/${flow?.tourId}/experience/steps/${currentStep?.url}`
              : null
          }
          backgroundImagePath={backgroundImagePath}
        />
      )}
      {innerSettings?.enableExitButton && (
        <Exitbar
          showFlowBar={showFlowBar}
          flow={flow}
          currentStep={currentStep}
          handleCloseFlowbar={openCloseDialog}
          isRemoteControlled={isRemoteControlled}
        />
      )}
      <Slide
        direction="up"
        in={!!(showFlowBar && currentStep && currentStep?.type === 'regular_step')}
        mountOnEnter
        unmountOnExit
      >
        <Stack
          className="flowbar-wrapper"
          sx={{
            ...FlowbarWrapperStyle,
            width: {
              xs: 'auto',
              sm: 'fit-content',
              md: currentStep?.title || description ? '800px' : 'fit-content',
            },
          }}
        >
          {hasAtLeastOneAction && (
            <Stack direction="row" sx={ActionsWrapperStyle}>
              {innerSettings?.enableStepsView && (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    ...ActionSectionStyle,
                    borderRight:
                      (innerSettings?.enableDistanceFromTheDistination &&
                        !currentStep?.hidePosBtn) ||
                      innerSettings?.enableExitButton
                        ? '1px solid rgba(255, 255, 255, 0.15)'
                        : 'none',
                  }}
                >
                  <Typography
                    fontSize="14px"
                    lineHeight="13.65px"
                    fontWeight={500}
                    textAlign="center"
                    whiteSpace="nowrap"
                  >
                    {`Step ${currentStepIndex}/${
                      pathLength
                      // steps?.filter((step) => step.type === 'regular_step').length
                    }`}
                  </Typography>
                </Stack>
              )}
              {innerSettings?.enableDistanceFromTheDistination && !currentStep?.hidePosBtn && (
                <Stack
                  spacing={1}
                  sx={{
                    ...ActionSectionStyle,
                    borderLeft: innerSettings?.enableStepsView
                      ? '1px solid rgba(255, 255, 255, 0.15);'
                      : 'none',
                    borderRight: innerSettings?.enableExitButton
                      ? '1px solid rgba(255, 255, 255, 0.15);'
                      : 'none',
                  }}
                >
                  <Typography
                    fontSize="14px"
                    lineHeight="13.65px"
                    fontWeight={500}
                    textAlign="center"
                    whiteSpace="nowrap"
                  >
                    {displayDistance}
                  </Typography>
                </Stack>
              )}
              {innerSettings?.enableExitButton && (
                <Stack
                  spacing={1}
                  sx={{
                    ...ActionSectionStyle,
                    borderLeft:
                      (innerSettings?.enableDistanceFromTheDistination &&
                        !currentStep?.hidePosBtn) ||
                      innerSettings?.enableStepsView
                        ? '1px solid rgba(255, 255, 255, 0.15)'
                        : 'none',
                  }}
                >
                  <IconButton sx={{ width: '28px' }} onClick={openCloseDialog}>
                    <FAIcon icon={X} size={19} color="#fff" />
                  </IconButton>
                </Stack>
              )}
            </Stack>
          )}
          <Stack
            direction="row"
            gap={2}
            sx={{
              ...FlowbarBodyStyle,
              borderRadius: hasAtLeastOneAction ? '20px 0px 20px 20px' : '20px',
              backgroundColor:
                flow?.flowSetting?.displayBarColors === 'preset'
                  ? FlowbarBodyStyle.background
                  : hexToRgbA(
                      flow?.flowSetting?.backgroundColor,
                      flow?.flowSetting?.backgroundOpacity,
                    ),
            }}
          >
            {(currentStep?.url || currentStep?.title || description) && (
              <Stack direction="row" spacing={2} flex="1 1 auto">
                {currentStep?.url && (
                  <Stack sx={ImageContainerStyle}>
                    <img
                      src={`${process.env.NEXT_PUBLIC_CDN}/tours/${flow?.tourId}/experience/steps/${currentStep?.url}`}
                      alt="flow-thumbnail"
                    />
                  </Stack>
                )}
                {(currentStep?.title || description) && (
                  <Stack>
                    {currentStep?.title && (
                      <Typography
                        fontSize="16px"
                        lineHeight="21px"
                        fontWeight={500}
                        sx={{
                          color:
                            flow?.flowSetting?.displayBarColors === 'preset'
                              ? ''
                              : flow?.flowSetting?.textColor,
                        }}
                      >
                        {currentStep?.title}
                      </Typography>
                    )}
                    {description && (
                      <Typography
                        fontSize="14px"
                        lineHeight="normal"
                        fontWeight={400}
                        color="rgba(255, 255, 255, 0.60)"
                        dangerouslySetInnerHTML={{ __html: description }}
                        sx={{
                          color:
                            flow?.flowSetting?.displayBarColors === 'preset'
                              ? ''
                              : flow?.flowSetting?.textColor,
                        }}
                      />
                    )}
                  </Stack>
                )}
              </Stack>
            )}

            <Stack
              direction="row"
              gap={1}
              flexWrap="wrap"
              flex="1 1 auto"
              justifyContent={nextButtons?.length <= 1 ? 'flex-end' : 'initial'}
            >
              {innerSettings?.enablePreviousButton &&
                currentStepIndex > 1 &&
                (DeviceManager.isDesktop() ||
                  (DeviceManager.isMobile() && nextButtons?.length <= 1)) && (
                  <IconButton
                    sx={{
                      ...PrevButtonStyle,
                      width: secondaryButtonExpanded ? 'auto' : '42px',
                      gap: 1,
                      flex: secondaryButtonExpanded ? { xs: '1 1 45%', sm: '1 1 auto' } : 'unset',
                      p: secondaryButtonExpanded ? '0 20px' : '8px',
                    }}
                    onClick={moveToPrevStep}
                  >
                    <FAIcon icon="faArrowLeft" size={20} color="#2055FF" />
                    {secondaryButtonExpanded && (
                      <Typography
                        fontSize="16px"
                        lineHeight="24px"
                        fontWeight={600}
                        color="#2055FF"
                      >
                        Back
                      </Typography>
                    )}
                  </IconButton>
                )}
              {DeviceManager.isMobile() && nextButtons?.length >= 3 && !isExpanded ? (
                <IconButton onClick={() => setIsExpanded(true)} sx={TriggerButtonStyle}>
                  <Typography fontSize="16px" lineHeight="26px" fontWeight={500} color="#FFF">
                    Choose
                  </Typography>
                  <FAIcon
                    icon="faChevronDown"
                    color="#fff"
                    size={16}
                    style={{ marginLeft: '10px' }}
                  />
                </IconButton>
              ) : (
                <Stack
                  direction="row"
                  gap={1}
                  flexWrap="wrap"
                  justifyContent="flex-start"
                  flex={{ xs: '1 1 70%', sm: nextButtons?.length >= 3 ? 1 : 'unset' }}
                  display={!nextButtons?.length ? 'none' : 'flex'}
                >
                  {nextButtons?.map((nb, i) => {
                    return (
                      <IconButton
                        disabled={nb?.disabled}
                        key={nb?.id}
                        onClick={() => handleNextButtonClick(nb)}
                        sx={{
                          ...TriggerButtonStyle,
                          background:
                            flow?.flowSetting?.displayBarColors === 'preset'
                              ? TriggerButtonStyle.background
                              : flow?.flowSetting?.nextButtonColor,
                          '&:hover': {
                            background:
                              flow?.flowSetting?.displayBarColors === 'preset'
                                ? TriggerButtonStyle['&:hover'].background
                                : flow?.flowSetting?.nextButtonColor,
                          },
                          flex: {
                            xs:
                              DeviceManager.isMobile &&
                              ((nextButtons.length === 3 && i === 2) ||
                                (nextButtons.length === 5 && i === 4))
                                ? '1 1 100%'
                                : '1 1 45%',
                            sm: 1,
                          },
                          '&:disabled': {
                            background: '#ccc',
                          },
                        }}
                      >
                        <Typography
                          fontSize="16px"
                          lineHeight="26px"
                          fontWeight={500}
                          sx={{
                            color:
                              flow?.flowSetting?.displayBarColors === 'preset'
                                ? '#FFF'
                                : flow?.flowSetting?.nextButtonTextColor,
                          }}
                        >
                          {nb?.buttonName}
                        </Typography>
                      </IconButton>
                    );
                  })}
                </Stack>
              )}
              {innerSettings?.enablePreviousButton &&
                currentStepIndex > 0 &&
                DeviceManager.isMobile() &&
                nextButtons?.length >= 2 && (
                  <IconButton
                    sx={{
                      ...PrevButtonStyle,
                      width: secondaryButtonExpanded ? 'auto' : '42px',
                      gap: 1,
                      flex: secondaryButtonExpanded ? { xs: '1 1 45%', sm: '1 1 auto' } : 'unset',
                      p: secondaryButtonExpanded ? '0 20px' : '8px',
                    }}
                    onClick={moveToPrevStep}
                  >
                    <FAIcon icon="faArrowLeft" size={20} color="#2055FF" />
                    {secondaryButtonExpanded && (
                      <Typography
                        fontSize="16px"
                        lineHeight="24px"
                        fontWeight={600}
                        color="#2055FF"
                      >
                        Back
                      </Typography>
                    )}
                  </IconButton>
                )}
              {innerSettings?.enableTeleportButton && !currentStep?.hidePosBtn && (
                <IconButton
                  sx={{
                    ...TPButtonStyle,
                    border:
                      flow?.flowSetting?.displayBarColors === 'preset'
                        ? TPButtonStyle.border
                        : `1px solid ${flow?.flowSetting?.nextButtonColor}`,
                    background:
                      flow?.flowSetting?.displayBarColors === 'preset'
                        ? TPButtonStyle.background
                        : flow?.flowSetting?.nextButtonTextColor,
                    '&:hover': {
                      background:
                        flow?.flowSetting?.displayBarColors === 'preset'
                          ? TPButtonStyle['&:hover'].background
                          : flow?.flowSetting?.nextButtonTextColor,
                    },
                    width: secondaryButtonExpanded ? 'auto' : '42px',
                    gap: 1,
                    flex: secondaryButtonExpanded ? { xs: '1 1 45%', sm: '1 1 auto' } : 'unset',
                    p: secondaryButtonExpanded ? '0 20px' : '8px',
                  }}
                  onClick={moveToStep}
                >
                  {secondaryButtonExpanded && (
                    <Typography fontSize="16px" lineHeight="24px" fontWeight={600} color="#2055FF">
                      {DeviceManager.isMobile() ? 'Position' : 'Position me'}
                    </Typography>
                  )}
                  <TeleportBtnIcon />
                </IconButton>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Slide>
      <FlowCloseDialog
        open={isCloseDialogOpen}
        onCancel={cancelCloseDialog}
        onAgree={onCloseAgree}
      />
      <FlowSwitchDialog
        flow={selectedFlow}
        open={isSwitchDialogOpen}
        onAgree={onSwitchAgree}
        onCancel={closeSwitchDialog}
      />
    </>
  );
};
